import { CollectionViewer } from '@angular/cdk/collections';
import { Signal, untracked, Injector, effect } from '@angular/core';
import { AgrDataSource } from '@next/core-lib/table-v2';
import { Observable, ReplaySubject, Subject, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { InseminationAdviceDto, sortInseminationAdvice } from '../models/insemination-advice.model';
import { ThresholdParams } from '../models/threshold-params.model';
import { InseminationAdviceService } from '../services/insemination-advice.service';

export class InseminationDataSource extends AgrDataSource<InseminationAdviceDto> {
  private readonly _destroy = new Subject<void>();
  private readonly _refresh = new ReplaySubject<void>(1);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this._loading$.asObservable();
  private readonly _rawData$ = new BehaviorSubject<InseminationAdviceDto[]>([]);

  private _sortColumn: 'earNumber' | 'age' | null = null;
  private _sortDirection: 'asc' | 'desc' | null = null;

  public readonly results$: Observable<readonly InseminationAdviceDto[]>;
  public readonly totalCount$: Observable<number>;

  constructor(
    private readonly _inseminationService: InseminationAdviceService,
    private readonly _thresholds: Signal<ThresholdParams | null>,
    injector: Injector,
  ) {
    super();

    this._refresh.next();

    effect(
      () => {
        this._thresholds();
        this._refresh.next();
      },
      { injector },
    );

    this._refresh
      .pipe(
        tap(() => {
          this.loadingSubject?.next(true);
          this._loading$.next(true);
        }),
        switchMap(() => {
          const currentThresholds = untracked(() => this._thresholds());
          return this._inseminationService
            .getInseminationAdvice(currentThresholds ?? undefined)
            .pipe(
              tap((data) => {
                this._rawData$.next(data || []);
              }),
              catchError(() => {
                // Prevent a single request failure from terminating the refresh stream.
                return EMPTY;
              }),
              finalize(() => {
                this.loadingSubject?.next(false);
                this._loading$.next(false);
              }),
            );
        }),
        takeUntil(this._destroy),
      )
      .subscribe();

    this.results$ = this._rawData$.pipe(
      map((arr) => this.sortAdvice(arr)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.totalCount$ = this.results$.pipe(
      map((arr) => arr.length),
      catchError(() => {
        return throwError(() => new Error('Failed to get total count'));
      }),
    );
  }

  public applySorting(): void {
    this._refresh.next();
  }

  public setSorting(column: 'earNumber' | 'age' | null, direction: 'asc' | 'desc' | null): void {
    this._sortColumn = column;
    this._sortDirection = direction;
    // Re-emit current data to trigger re-sort, NOT _refresh.next()
    this._rawData$.next(this._rawData$.value);
  }

  private sortAdvice(data: InseminationAdviceDto[]): InseminationAdviceDto[] {
    let sorted = [...data];

    if (this._sortColumn && this._sortDirection) {
      sorted.sort((a, b) => {
        let comparison = 0;

        if (this._sortColumn === 'earNumber') {
          const aVal = a.earNumber || '';
          const bVal = b.earNumber || '';
          comparison = aVal.localeCompare(bVal, undefined, { numeric: true });
        } else if (this._sortColumn === 'age') {
          comparison = (a.ageInMonths || 0) - (b.ageInMonths || 0);
        }

        return this._sortDirection === 'asc' ? comparison : -comparison;
      });
      return sorted;
    }

    return sortInseminationAdvice(sorted);
  }

  public setLoading(loading: boolean): void {
    this.loadingSubject?.next(loading);
  }

  connect(_: CollectionViewer): Observable<readonly InseminationAdviceDto[]> {
    return this.results$;
  }

  disconnect(_: CollectionViewer): void {
    if (!this._destroy.closed) {
      this._destroy.next();
      this._destroy.complete();
    }
  }
}
